import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
    Buildings,
    Receipt,
    CreditCard,
    Truck,
    Users,
    ShieldCheck,
    CaretRight,
    CaretLeft,
    CurrencyCircleDollar,
    Wallet,
} from '@phosphor-icons/react';
import { MERCHANT_ROUTES, useAuthStore } from '@dukene/shared';

export default function SettingsLayout() {
    const location = useLocation();
    const { user } = useAuthStore();
    const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 768);

    const isOwner =
        user?.role === 'OWNER' ||
        user?.role === 'SUPER_ADMIN' ||
        user?.role === 'MERCHANT' ||
        user?.membershipRole === 'OWNER' ||
        user?.membershipRole === 'CO_OWNER';

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setIsCollapsed(true);
            } else {
                setIsCollapsed(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (window.innerWidth < 768) {
            setIsCollapsed(true);
        }
    }, [location.pathname]);

    const settingsNavigation = [
        {
            label: 'Store details',
            icon: (props: any) => <Buildings weight="fill" {...props} />,
            href: MERCHANT_ROUTES.SETTINGS,
        },
        ...(isOwner ? [
            {
                label: 'Plan and billing',
                icon: (props: any) => <Receipt weight="fill" {...props} />,
                href: '/settings/billing',
            },
            {
                label: 'Credits wallet',
                icon: (props: any) => <Wallet weight="fill" {...props} />,
                href: '/settings/wallet',
            },
            {
                label: 'Pricing',
                icon: (props: any) => <CurrencyCircleDollar weight="fill" {...props} />,
                href: '/settings/pricing',
            },
        ] : []),
        {
            label: 'Payments',
            icon: (props: any) => <CreditCard weight="fill" {...props} />,
            href: MERCHANT_ROUTES.SETTINGS_PAYMENTS,
        },
        {
            label: 'Shipping and delivery',
            icon: (props: any) => <Truck weight="fill" {...props} />,
            href: MERCHANT_ROUTES.SETTINGS_SHIPPING,
        },
        {
            label: 'Supplier network',
            icon: (props: any) => <Users weight="fill" {...props} />,
            href: '/settings/suppliers',
        },
        {
            label: 'Users and permissions',
            icon: (props: any) => <ShieldCheck weight="fill" {...props} />,
            href: '/settings/managers',
        }
    ];

    const isActive = (href: string) => {
        return location.pathname === href;
    };

    return (
        <div className="flex h-full bg-white dark:bg-slate-950 overflow-hidden">
            {/* Settings Sub-sidebar - Shopify Polaris Style */}
            <aside
                className={`border-r border-[#D2D5D9] dark:border-slate-800 bg-[#F1F2F4] dark:bg-slate-900 flex flex-col shrink-0 transition-all duration-200 ${
                    isCollapsed ? 'w-14' : 'w-60'
                }`}
            >
                {/* Header */}
                <div
                    className={`px-3.5 py-3 border-b border-[#D2D5D9] dark:border-slate-800 flex items-center ${
                        isCollapsed ? 'justify-center' : 'justify-between'
                    }`}
                >
                    {!isCollapsed && (
                        <div className="min-w-0">
                            <h2 className="text-[14px] font-bold text-[#202223] dark:text-white leading-5">Settings</h2>
                        </div>
                    )}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="p-1 text-[#5C5F62] hover:text-[#202223] dark:text-slate-400 dark:hover:text-white hover:bg-[#E1E4E8] dark:hover:bg-slate-800 rounded transition-colors shrink-0"
                        title={isCollapsed ? 'Expand settings menu' : 'Collapse settings menu'}
                    >
                        {isCollapsed ? <CaretRight className="w-4 h-4" weight="bold" /> : <CaretLeft className="w-4 h-4" weight="bold" />}
                    </button>
                </div>

                {/* Navigation Menu */}
                <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-0.5 custom-scrollbar">
                    {settingsNavigation.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.href);

                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                title={isCollapsed ? item.label : undefined}
                                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] leading-5 transition-colors ${
                                    active
                                        ? 'bg-[#E1E4E8] dark:bg-slate-800 text-[#202223] dark:text-white font-semibold'
                                        : 'text-[#202223] dark:text-slate-300 hover:bg-[#EBEBEB] dark:hover:bg-slate-800/60'
                                } ${isCollapsed ? 'justify-center' : ''}`}
                            >
                                <Icon className="w-[15px] h-[15px] text-[#5C5F62] dark:text-slate-400 shrink-0" />
                                {!isCollapsed && <span className="truncate">{item.label}</span>}
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer Help Button */}
                <div className="p-2.5 border-t border-[#D2D5D9] dark:border-slate-800 bg-[#EBEBEB]/50 dark:bg-slate-900/50">
                    <button
                        onClick={() => alert('Opening Dukene Merchant Support Center...')}
                        title="Support Center"
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] font-medium text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-white hover:bg-[#E1E4E8] dark:hover:bg-slate-800 transition-colors ${
                            isCollapsed ? 'justify-center' : ''
                        }`}
                    >
                        <CaretRight weight="fill" className="w-3.5 h-3.5 shrink-0 opacity-40" />
                        {!isCollapsed && <span className="truncate">Support & Help</span>}
                    </button>
                </div>
            </aside>

            {/* Settings Content Area */}
            <main className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950 custom-scrollbar">
                <div className="p-4 sm:p-6 md:p-8 max-w-5xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
